/**
 * Manual ordering by fractional-index rank - ONE definition for every surface
 * that sorts by hand: a board's lanes (`views.payload.order`, docs/VIEWS.md)
 * and a lens's checklist rail (`checklist.order`, docs/CHECKLIST.md §7). The
 * map is sparse: `itemId -> rank` (a `fractional-indexing` key, lexicographic),
 * so a drag writes one item's rank, a rank never has to shift to make room,
 * and two people's drags merge rather than clobber. At read time ranked
 * items come first by rank, then anything without a rank tails by
 * `createdAt` - an item nobody has dragged yet simply joins in creation order
 * while the manual arrangement above it stays stable.
 *
 * The drop side (which rank a landed item gets, against neighbours that may
 * be unranked) is `rankPatchForDrop` in `src/lib/board-order.ts`; it computes
 * over the order this module defines. Pure, no I/O.
 *
 * A PARENT's order of its LIST is the other kind of hand order and is NOT a
 * client rank map: since 2026-11-03 (docs/CHECKLIST.md §2) each item holds one
 * rank map over its list - its checklist's lines and its direct child items -
 * on its own `checklist` module (`src/domain/checklist.ts`), where it held the
 * children's ranks on their parent edges from 2026-09-12. `compareListSlots`
 * is the list's one total rule, and `decideListPlacement` computes the rank on
 * the server, against the whole list, from a move ("this entry after that
 * one") - see below.
 */

import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';
import { m } from '@/i18n';
import { errOf, ok, type Result } from './types';

export type RankMap = Record<string, string>;
/** A drag's patch: `id -> rank`, or `null` to clear an item's manual rank. */
export type RankPatch = Record<string, string | null>;

/** A stored rank map read back: only non-empty string ranks survive, so a bad
 *  value can never put an item in an undefined slot. The one validator every
 *  stored order goes through (a board's kanban and checklist orders, a view's
 *  checklist rail). */
export function normalizeRankMap(input: unknown): RankMap {
  const order: RankMap = {};
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    for (const [id, rank] of Object.entries(input as Record<string, unknown>)) {
      if (typeof rank === 'string' && rank.length > 0) order[id] = rank;
    }
  }
  return order;
}

/** Merge a rank patch into an order, returning the next map: keys merge, a
 *  null or empty rank clears. The write spine and tests share this one
 *  definition of "apply a drag". */
export function mergeRankPatch(order: RankMap, patch: RankPatch): RankMap {
  const next = { ...order };
  for (const [id, rank] of Object.entries(patch)) {
    if (rank === null || rank.length === 0) delete next[id];
    else next[id] = rank;
  }
  return next;
}

export interface Ranked {
  rank: string | null;
  createdAt: Date;
}

/** Ranked first by rank (string order), then the unranked tail by createdAt. */
export function compareRanked(a: Ranked, b: Ranked): number {
  if (a.rank !== null && b.rank !== null) return a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0;
  if (a.rank !== null) return -1;
  if (b.rank !== null) return 1;
  return a.createdAt.getTime() - b.createdAt.getTime();
}

/** The ranked-first order of `items` under `order`. Stable for equal keys;
 *  the input is left untouched. */
export function sortByRank<T extends { id: string; createdAt: Date }>(
  items: readonly T[],
  order: RankMap,
): T[] {
  const ranked = (t: T): Ranked => ({ rank: order[t.id] ?? null, createdAt: t.createdAt });
  return [...items].sort((a, b) => compareRanked(ranked(a), ranked(b)));
}

/**
 * One child of a parent, as the parent's order sees it: its rank in the
 * parent's list (`checklist.order` on the parent's module, 2026-11-03), when
 * it was attached, and the edge id.
 */
export interface ChildSlot {
  childId: string;
  rank: string | null;
  attachedAt: Date;
  edgeId: string;
}

/**
 * One ENTRY of an item's list (docs/CHECKLIST.md §2, ruled 2026-11-03): a line
 * of its checklist or a direct child item, both ranked in the item's one rank
 * map. `at` orders the unranked tail within a kind: a child's attach time (ms),
 * a line's place among the lines (its creation order); `tie` is the edge id or
 * the line id, so no two entries ever compare equal.
 */
export interface ListSlot {
  id: string;
  kind: 'child' | 'line';
  rank: string | null;
  at: number;
  tie: string;
}

/**
 * THE order of an item's list - one rule for every surface that draws it (the
 * item page's list, a checklist board, the agents' work tree): ranked entries
 * first by rank, then the unranked CHILDREN by when each was attached, then the
 * unranked LINES in the order they were written, and the tie id last. A total
 * order is what lets two surfaces with different sight agree on the relative
 * place of the entries they both show.
 */
export function compareListSlots(a: ListSlot, b: ListSlot): number {
  if (a.rank !== null && b.rank !== null && a.rank !== b.rank) return a.rank < b.rank ? -1 : 1;
  if (a.rank !== null && b.rank === null) return -1;
  if (a.rank === null && b.rank !== null) return 1;
  if (a.kind !== b.kind) return a.kind === 'child' ? -1 : 1;
  if (a.at !== b.at) return a.at - b.at;
  return a.tie < b.tie ? -1 : a.tie > b.tie ? 1 : 0;
}

/** A child slot as a list entry. */
export function childListSlot(c: ChildSlot): ListSlot {
  return { id: c.childId, kind: 'child', rank: c.rank, at: c.attachedAt.getTime(), tie: c.edgeId };
}

/**
 * The order of a parent's CHILDREN alone (a parent with no lines, the agents'
 * work tree): `compareListSlots` over child entries, so it never disagrees with
 * the list the children share with lines.
 */
export function compareChildSlots(a: ChildSlot, b: ChildSlot): number {
  return compareListSlots(childListSlot(a), childListSlot(b));
}

/**
 * Ranks for every entry of `slots` in their current order, for the entries
 * whose rank would change: a fresh run of keys when any entry is unranked
 * (after which the list is fully ranked and every later move is one key),
 * otherwise nothing. A promotion and a demotion call it first, so the entry
 * they swap keeps its exact place among unranked neighbours.
 */
export function rankedInPlace(slots: readonly ListSlot[]): RankPatch {
  const current = [...slots].sort(compareListSlots);
  if (current.every((c) => c.rank !== null)) return {};
  const keys = generateNKeysBetween(null, null, current.length);
  const patch: RankPatch = {};
  current.forEach((c, i) => {
    if (c.rank !== keys[i]) patch[c.id] = keys[i]!;
  });
  return patch;
}

/**
 * Place one entry directly after another (`afterId`), or first (`null`),
 * against ALL of the list's entries - not the neighbours one surface happens
 * to show. The server calls this under the parent's row lock, so every writer
 * places into the same order and nobody's rank is computed from a partial view
 * (a client computing ranks against the siblings it could see wrote ties with
 * hidden ones, 2026-09-12 review).
 *
 * Returns the ranks that change: one fresh key between the new neighbours when
 * both are ranked and distinct, otherwise a renumber of the whole list in its
 * new order. Refused: an entry or a neighbour that is not on this list, and a
 * move that leaves the order as it was (`no_change`).
 */
export function decideListPlacement(
  slots: readonly ListSlot[],
  id: string,
  afterId: string | null,
): Result<RankPatch> {
  const current = [...slots].sort(compareListSlots);
  const moved = current.find((c) => c.id === id);
  if (!moved) return errOf('invalid_input', m('itemErrors.order.notAChild'));
  if (afterId === id) return errOf('invalid_input', m('itemErrors.order.followsItself'));
  const rest = current.filter((c) => c.id !== id);
  let at = 0;
  if (afterId !== null) {
    const i = rest.findIndex((c) => c.id === afterId);
    if (i < 0) return errOf('invalid_input', m('itemErrors.order.notAChild'));
    at = i + 1;
  }
  const next = [...rest.slice(0, at), moved, ...rest.slice(at)];
  if (next.every((c, i) => c.id === current[i]!.id)) {
    return errOf('no_change', m('common.error.orderUnchanged'));
  }

  const before = next[at - 1];
  const after = next[at + 1];
  const leftOk = before === undefined || before.rank !== null;
  const rightOk = after === undefined || after.rank !== null;
  const distinct = before?.rank == null || after?.rank == null || before.rank < after.rank;
  if (leftOk && rightOk && distinct) {
    const key = generateKeyBetween(before?.rank ?? null, after?.rank ?? null);
    return ok({ [id]: key });
  }
  const keys = generateNKeysBetween(null, null, next.length);
  const patch: RankPatch = {};
  next.forEach((c, i) => {
    if (c.rank !== keys[i]) patch[c.id] = keys[i]!;
  });
  return ok(patch);
}

/**
 * Place one child directly after a sibling (`afterChildId`), or first (`null`),
 * among a parent's children alone: `decideListPlacement` over child entries.
 */
export function decideChildPlacement(
  slots: readonly ChildSlot[],
  childId: string,
  afterChildId: string | null,
): Result<RankPatch> {
  return decideListPlacement(slots.map(childListSlot), childId, afterChildId);
}

/**
 * Put `ids` FIRST, in exactly that order, ahead of every other ranked item -
 * the hand order said as a sequence rather than a drag (the agent tools'
 * `view_place`, which names the lines it wants on top and never a
 * neighbour's rank). The keys fall strictly below the lowest rank of any item
 * NOT named, so the rest keep their relative order behind them; an id named
 * twice keeps its first place. Returns the patch for the named ids only.
 */
export function ranksAhead(order: RankMap, ids: readonly string[]): RankPatch {
  const named = [...new Set(ids)];
  if (named.length === 0) return {};
  const others = Object.entries(order)
    .filter(([id]) => !named.includes(id))
    .map(([, rank]) => rank)
    .sort();
  const keys = generateNKeysBetween(null, others[0] ?? null, named.length);
  const patch: RankPatch = {};
  named.forEach((id, i) => {
    patch[id] = keys[i]!;
  });
  return patch;
}
